import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';
import { useBewerber, useUpdateBewerber } from '../../api/bewerber';
import { BewerberForm } from '../../components/bewerber/BewerberForm';
import { PflegenDocumentPanel } from '../../components/bewerber/PflegenDocumentPanel';
import { BewerberStatus, CreateBewerberDto } from '../../types/bewerber';

const FORM_ID = 'bewerber-bearbeiten-form';

export function BewerberBearbeitenPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data, isLoading, error } = useBewerber(id!);
  const updateMutation = useUpdateBewerber();

  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const handleSubmit = async (formData: CreateBewerberDto) => {
    try {
      const result = await updateMutation.mutateAsync({ id: id!, data: formData });
      toast.success('Bewerber erfolgreich aktualisiert');
      setIsDirty(false);

      // If the saved Bewerber is in "Eingeladen", offer to advance to "Vorstellung".
      if (result.data.status === BewerberStatus.EINGELADEN) {
        const moveToVorstellung = await confirm({
          title: 'Nach Vorstellung verschieben?',
          message:
            'Der Bewerber befindet sich im Status "Eingeladen". Möchten Sie ihn nach "Vorstellung" verschieben?',
          confirmLabel: 'Nach Vorstellung',
          cancelLabel: 'Nein',
        });
        if (moveToVorstellung) {
          await updateMutation.mutateAsync({
            id: id!,
            data: { status: BewerberStatus.VORSTELLUNG },
          });
          toast.success('Bewerber nach Vorstellung verschoben');
        }
        // Answering the prompt ends the edit flow either way, so leave for the
        // detail view regardless of the answer.
        navigate(`/bewerber/${id}`);
      }
    } catch (err) {
      console.error('Failed to update bewerber:', err);
      toast.error('Fehler beim Aktualisieren des Bewerbers');
    }
  };

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: 'Ungespeicherte Änderungen',
        message: 'Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?',
        confirmLabel: 'Verlassen',
        cancelLabel: 'Abbrechen',
        variant: 'warning',
      });
      if (!confirmed) return;
    }
    navigate(`/bewerber/${id}`);
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">Fehler beim Laden des Bewerbers</p>
          <Link to="/bewerber" className="text-primary-600 hover:text-primary-900">
            Zurück zur Liste
          </Link>
        </div>
      </div>
    );
  }

  const bewerber = data.data;
  const documents = bewerber.dokumente || [];

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-20">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center">
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 mr-3"
              title="Zurück"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {bewerber.firstName} {bewerber.lastName} bearbeiten
              </h1>
              {isDirty && (
                <span className="text-xs text-amber-600">Ungespeicherte Änderungen</span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="submit"
              form={FORM_ID}
              disabled={updateMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Schließen
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - Responsive: full width on mobile, 50/50 split on lg+ */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Form (full width on mobile, half on lg+) */}
        <div className="w-full lg:w-1/2 overflow-y-auto p-4 lg:border-r border-gray-200 bg-gray-50">
          <BewerberForm
            formId={FORM_ID}
            initialData={bewerber}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            isLoading={updateMutation.isPending}
            hideFooter
            onDirtyChange={setIsDirty}
          />
        </div>

        {/* Right Panel - Document Preview (hidden on mobile, visible on lg+) */}
        <div className="hidden lg:block lg:w-1/2 p-4 bg-gray-100 overflow-y-auto">
          <PflegenDocumentPanel
            bewerberId={bewerber.id}
            documents={documents}
            selectedIndex={selectedDocIndex}
            onSelectIndex={setSelectedDocIndex}
          />
        </div>
      </div>
    </div>
  );
}
